import { useCallback, useEffect, useRef, useState } from "react";
import { IonSkeletonText } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { BoxShadows, Button } from "swing-components";

import { UploadAnswerType } from "~onboarding/utils";
import { GET } from "~utils";

type UploadAnswerProps = Omit<UploadAnswerType, "type"> & {
  /**
   * The function to be called when a document is uploaded
   */
  onChange: (value: string) => void;
  /**
   * Disables the ability to upload documents
   */
  isReadOnly?: boolean;
};

export function UploadAnswer(props: UploadAnswerProps) {
  const { label, onChange, isReadOnly } = props;

  // This query will upsert the folder name into box, and
  // return the folder name and an auth token that can be
  // subsequently used to upload files.
  //
  // The folder name defaults to the user's db-id.
  const onComplete = useCallback((fileId: string) => onChange(fileId), [onChange]);

  const { data: boxValues } = useQuery({
    queryKey: ["getBoxValues"],
    queryFn: () => GET("/api/sub/upload-metadata"),
    select: (data) => data.data,
  });

  /***** Render *****/
  if (!boxValues) {
    return (
      <IonSkeletonText
        animated={true}
        css={{
          height: 36,
          width: "100%",
          // Derived from Ionic styles
          borderRadius: 30,
        }}
      />
    );
  }

  return (
    <BoxUploader
      label={label}
      folderId={boxValues.folderId}
      accessToken={boxValues.accessToken}
      onComplete={onComplete}
      isReadOnly={isReadOnly}
    />
  );
}

/********** Helper Component **********/
type BoxUploaderProps = {
  label: string;
  folderId: string;
  accessToken: string;
  onComplete: (fileId: string) => void;
  isReadOnly?: boolean;
};

/**
 * Displays the Box uploader UI which deals with uploading files to Box.
 * When the upload is complete, the fileId is passed to the parent component via `onComplete`.
 *
 * See https://developer.box.com/guides/embed/ui-elements/uploader
 */
function BoxUploader(props: BoxUploaderProps) {
  const { label, folderId, accessToken, onComplete, isReadOnly } = props;

  /***** State *****/
  const [isOpen, setIsOpen] = useState(false);

  /***** Refs *****/
  // Initiating the box uploader
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contentUploaderRef = useRef<any>(null);

  /***** Handlers *****/
  function handleClick() {
    // Handling Storybook mocking since we generate a fake access token and folder id and thus the upload will actually not work.
    if (window?.IS_STORYBOOK === true) {
      return onComplete("fake-file-id");
    }

    // Mount the upload container
    setIsOpen(true);
    // Show the uploader
    contentUploaderRef.current.show(folderId, accessToken, {
      container: "#upload-modal",
      fileLimit: 1,
    });
  }

  useEffect(() => {
    const contentUploader = new Box.ContentUploader();
    contentUploaderRef.current = contentUploader;

    // The `complete` event returns an array of file metadata
    // https://developer.box.com/guides/embed/ui-elements/uploader/#events
    contentUploader.addListener("complete", (data: { id: string }[]) => {
      setIsOpen(false);
      onComplete(data[0].id);
    });

    contentUploader.addListener("close", () => {
      setIsOpen(false);
    });

    return () => {
      contentUploader.removeAllListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Button
        type="primary"
        status={isReadOnly ? "disabled" : "enabled"}
        onClick={handleClick}
        isFluid
      >
        {label}
      </Button>

      {/* Uploader container */}
      <div
        id="upload-modal"
        css={{
          display: isOpen === true ? "block" : "none",
          /* Fixed over absolute so that we handle situations where the page has a scroll. */
          position: "fixed",
          zIndex: 1,
          height: "80%",
          width: "80%",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: 8,
          boxShadow: BoxShadows.hover,
          // The direct div child must also have a border radius so that it's shown
          "& > div": {
            borderRadius: 8,
          },
        }}
      />
    </div>
  );
}
